/**
 * Label suggestions for a new connector (pure, unit tested). Musical road-map terms are the same in
 * both languages; "Áfram" ("Continue") and "Vers" ("Verse") are passed in translated. A jump
 * backwards (to an earlier page or higher up on the same page) suggests repeats first; a forward jump
 * suggests Coda / Áfram first. Labels already used on this sheet come first, and "Vers N+1" follows
 * the highest verse used (in either language).
 */
import { MAX_LABEL_CHARS } from '../../shared/annotations';

export interface LabelPoint {
  page: number;
  y: number;
}

export function isBackwardJump(source: LabelPoint, target: LabelPoint): boolean {
  return target.page < source.page || (target.page === source.page && target.y < source.y);
}

const VERSE = /^(?:vers|verse)\s*(\d{1,2})$/i;

/** Next verse label after the highest "Vers N" / "Verse N" in use (default "<verseWord> 2"). */
export function nextVerseLabel(used: readonly string[], verseWord: string): string {
  let max = 1;
  for (const l of used) {
    const m = VERSE.exec(l.trim());
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `${verseWord} ${max + 1}`;
}

export function suggestConnectorLabels(opts: {
  source: LabelPoint;
  target: LabelPoint;
  /** Labels of existing connectors on this sheet, most recent first. */
  used: readonly string[];
  /** Translated "Áfram". */
  forwardWord: string;
  /** Translated "Vers". */
  verseWord: string;
  max?: number;
}): string[] {
  const verse = nextVerseLabel(opts.used, opts.verseWord);
  const base = isBackwardJump(opts.source, opts.target)
    ? ['D.C.', 'D.S.', verse, 'Segno', opts.forwardWord, 'Coda', 'Fine']
    : ['Coda', opts.forwardWord, 'Fine', 'Segno', 'D.S.', 'D.C.', verse];
  const recent = opts.used.map((l) => l.trim()).filter((l) => l.length > 0 && l.length <= MAX_LABEL_CHARS).slice(0, 3);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const l of [...recent, ...base]) {
    const k = l.toLocaleLowerCase('is');
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(l);
  }
  return out.slice(0, opts.max ?? 8);
}
